import { ArrowLeftRight } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";
import { formatResult } from "../lib/convert";
import { convert, getUnit, type UnitCategory } from "../lib/units";

const selectClass =
  "h-10 w-full rounded-md border border-border bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

/** Config-driven converter for one category. Default inputs give a deterministic
 * server-rendered result (no CLS / hydration mismatch). */
export function UnitConverter({ category }: { category: UnitCategory }) {
  const uid = useId();
  const [value, setValue] = useState(category.defaultValue);
  const [fromId, setFromId] = useState(category.defaultFrom);
  const [toId, setToId] = useState(category.defaultTo);
  const locale = getLocale();

  const result = useMemo(() => {
    const n = value.trim() === "" ? Number.NaN : Number(value);
    return convert(category, n, fromId, toId);
  }, [category, value, fromId, toId]);

  const swap = () => {
    setFromId(toId);
    setToId(fromId);
  };

  const fromLabel = getUnit(category, fromId)?.label ?? fromId;
  const toLabel = getUnit(category, toId)?.label ?? toId;
  const formatted = formatResult(result, locale);

  return (
    <div className="rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
      <div className="grid items-end gap-4 sm:grid-cols-[1fr_auto_1fr]">
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor={`${uid}-value`}>{m.uc_from()}</Label>
            <Input
              id={`${uid}-value`}
              type="number"
              inputMode="decimal"
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
          </div>
          <select
            aria-label={m.uc_from_unit()}
            className={selectClass}
            value={fromId}
            onChange={(e) => setFromId(e.target.value)}
          >
            {category.units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.label}
              </option>
            ))}
          </select>
        </div>

        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={swap}
          aria-label={m.uc_swap()}
          className="self-center sm:mb-12"
        >
          <ArrowLeftRight className="h-4 w-4" />
        </Button>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor={`${uid}-result`}>{m.uc_to()}</Label>
            <Input
              id={`${uid}-result`}
              readOnly
              value={formatted}
              aria-live="polite"
              data-testid="uc-result"
              className="font-semibold tabular-nums"
            />
          </div>
          <select
            aria-label={m.uc_to_unit()}
            className={selectClass}
            value={toId}
            onChange={(e) => setToId(e.target.value)}
          >
            {category.units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {result !== null && value.trim() !== "" && (
        <p className="mt-5 text-sm text-muted-foreground">
          {m.uc_summary({
            value: Number(value).toLocaleString(locale),
            from: fromLabel,
            result: formatted,
            to: toLabel,
          })}
        </p>
      )}
    </div>
  );
}
